import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";

const FONT_MIN = 10;
const FONT_MAX = 20;
const FONT_DEFAULT = 12;
const FONT_KEY = "fcr.devops.fontSize";
const PING_MS = 25_000;

type QuickAction = { id: string; label: string; cmd: string; title?: string };
type QuickGroup = { id: string; label: string; actions: QuickAction[] };
type QuickDomain = { id: string; label: string; dir: string; groups: QuickGroup[] };

/** Three-level quick nav: domain → group → actions (cwd = scripts/). */
const QUICK_TREE: QuickDomain[] = [
  {
    id: "mqtt",
    label: "mqtt",
    dir: "mqtt",
    groups: [
      {
        id: "topics",
        label: "topics",
        actions: [
          {
            id: "topics-list",
            label: "list catalog",
            cmd: "python mqtt_topics.py",
            title: "List catalog topics (FCR-v1 cache)",
          },
          {
            id: "topics-retained",
            label: "catalog retained",
            cmd: "python mqtt_topics.py --retained",
            title: "Catalog topics marked retained: true",
          },
          {
            id: "topics-sync",
            label: "sync from GitHub",
            cmd: "python mqtt_topics.py --sync",
            title: "Refresh local catalog from bluecurrent/FCR-v1",
          },
        ],
      },
      {
        id: "state",
        label: "state",
        actions: [
          {
            id: "state-count",
            label: "count by state",
            cmd: "python mqtt_state.py --count --timeout 5",
            title: "Tally online/offline/empty for gw/ws/state/+",
          },
          {
            id: "state-count-samples",
            label: "count + samples",
            cmd: "python mqtt_state.py --count --timeout 5 -n 5",
            title: "State tally plus first 5 payloads",
          },
          {
            id: "state-list-offline",
            label: "list offline",
            cmd: "python mqtt_state.py --list --state offline --timeout 5 -n 5 --ids --limit 200",
            title: "Offline chargers: first 5 payloads + remaining ids",
          },
          {
            id: "state-list-online",
            label: "list online",
            cmd: "python mqtt_state.py --list --state online --timeout 5 -n 5 --ids --limit 200",
            title: "Online chargers: first 5 payloads + remaining ids",
          },
          {
            id: "state-list-offline-star",
            label: "list offline *.BCU*",
            cmd: "python mqtt_state.py --list --state offline --id-glob '*.BCU*' --timeout 5 -n 5 --ids --limit 200",
            title: "Offline *.BCU* simulators: payloads + ids",
          },
        ],
      },
      {
        id: "retained",
        label: "retained",
        actions: [
          {
            id: "retained-list",
            label: "list prefixes",
            cmd: "python mqtt_retained.py --list --timeout 5",
            title: "List retained topic prefixes (broker dump, 5s)",
          },
          {
            id: "retained-count-state",
            label: "count gw/ws/state",
            cmd: "python mqtt_retained.py --count -t 'gw/ws/state/+' --timeout 30",
            title: "Full count with kind/state/family breakdown",
          },
          {
            id: "retained-count-star",
            label: "count *.BCU*",
            cmd: "python mqtt_retained.py --count -t 'gw/ws/state/+' --timeout 30 --id-glob '*.BCU*'",
            title: "Count star-prefix simulator retains",
          },
          {
            id: "retained-delete-star-dry",
            label: "delete *.BCU* (dry)",
            cmd: "python mqtt_retained.py --delete -t 'gw/ws/state/+' --timeout 45 --id-glob '*.BCU*'",
            title: "Dry-run delete *.BCU* retains",
          },
          {
            id: "retained-delete-offline-dry",
            label: "clear offline gw/ws/state (dry)",
            cmd: "python mqtt_retained.py --delete -t 'gw/ws/state/+' --timeout 45 --state offline",
            title: "Dry-run delete offline gw/ws/state retains",
          },
        ],
      },
    ],
  },
  {
    id: "charger-selection",
    label: "charger selection",
    dir: "charger-selection",
    groups: [
      {
        id: "online",
        label: "online",
        actions: [
          {
            id: "cs-edit",
            label: "nano chargers.txt",
            cmd: "nano ./chargers.txt",
            title: "Paste charger IDs into ./chargers.txt",
          },
          {
            id: "cs-check",
            label: "check online",
            cmd: "./check_online.py --file ./chargers.txt --timeout 5",
            title: "Check which IDs in ./chargers.txt are online (gw/ws/state)",
          },
          {
            id: "cs-cat",
            label: "show list",
            cmd: "sed -n '1,80p' ./chargers.txt",
            title: "Show first lines of ./chargers.txt",
          },
        ],
      },
    ],
  },
  {
    id: "charger-commands",
    label: "charger commands",
    dir: "charger-commands",
    groups: [
      {
        id: "batch",
        label: "batch cfg",
        actions: [
          {
            id: "cfg-help",
            label: "python batch_cfg.py --help",
            cmd: "python batch_cfg.py --help",
            title: "--mvsi SEC  --mvsm IVEPO  --family BCU12|BCU34|PBLR  --status free|paused|charging|all  --limit N  --apply",
          },
          {
            id: "cfg-apply",
            label: "python batch_cfg.py --mvsi 5 --mvsm IVPEO --family BCU34 --status paused --limit 10 --apply",
            cmd: "python batch_cfg.py --mvsi 5 --mvsm IVPEO --family BCU34 --status paused --limit 10 --apply",
            title: "Paste only. Edit flags, Enter. Then y to send. Default --status is free; this example uses paused.",
          },
        ],
      },
      {
        id: "batch-reset",
        label: "batch reset",
        actions: [
          {
            id: "rst-help",
            label: "python batch_reset.py --help",
            cmd: "python batch_reset.py --help",
            title: "--family BCU12|BCU34|PBLR (default BCU34)  --status free|paused|charging|all  --limit N  --subset ID,ID  --nurse yes|no  --apply",
          },
          {
            id: "rst-apply",
            label: "python batch_reset.py --family BCU34 --status free --limit 10 --subset BCU300050,BCU300051,BCU300055,BCU300062,BCU300066,BCU300067,BCU300069,BCU300128,BCU300130,BCU300143 --nurse yes --apply",
            cmd: "python batch_reset.py --family BCU34 --status free --limit 10 --subset BCU300050,BCU300051,BCU300055,BCU300062,BCU300066,BCU300067,BCU300069,BCU300128,BCU300130,BCU300143 --nurse yes --apply",
            title: "Paste only. Edit flags, Enter. Preview without --apply. With --apply, --nurse yes asks y/N/q per charger.",
          },
        ],
      },
    ],
  },
  {
    id: "infer-charger-state",
    label: "infer charger state",
    dir: "infer-charger-state",
    groups: [
      {
        id: "trigger-message",
        label: "TriggerMessage",
        actions: [
          {
            id: "trg-help",
            label: "help",
            cmd: "python trigger_message.py --help",
            title: "--family BCU12|BCU34|PBLR  --message MeterValues|StatusNotification  --limit N  --apply",
          },
          {
            id: "trg-preview",
            label: "python trigger_message.py --family BCU34",
            cmd: "python trigger_message.py --family BCU34",
            title: "Paste only. Preview: online chargers of the family.",
          },
          {
            id: "trg-mv-apply",
            label: "python trigger_message.py --family BCU34 --message MeterValues --apply",
            cmd: "python trigger_message.py --family BCU34 --message MeterValues --apply",
            title: "Paste only. Edit --family, Enter, then y. Fresh MeterValues for listen MVs.",
          },
          {
            id: "trg-sn-apply",
            label: "python trigger_message.py --family BCU34 --message StatusNotification --apply",
            cmd: "python trigger_message.py --family BCU34 --message StatusNotification --apply",
            title: "Paste only. StatusNotification makes ocpp-core republish ocpp/sts/inferred from the status word.",
          },
        ],
      },
      {
        id: "listen-mv",
        label: "listen MVs",
        actions: [
          {
            id: "mv-listen-help",
            label: "help",
            cmd: "python listen_mv.py --help",
            title: "--seconds 150  --current-a 1  --power-w 230  --family …  --apply",
          },
          {
            id: "mv-listen-preview",
            label: "python listen_mv.py --seconds 150 --current-a 1 --power-w 230",
            cmd: "python listen_mv.py --seconds 150 --current-a 1 --power-w 230",
            title: "Paste only. Listen with countdown, list chargers that would become C. Publishes nothing.",
          },
          {
            id: "mv-listen-apply",
            label: "python listen_mv.py --seconds 150 --current-a 1 --power-w 230 --apply",
            cmd: "python listen_mv.py --seconds 150 --current-a 1 --power-w 230 --apply",
            title: "Paste only. After the window: y publishes retained state C (never A/B/E/F), only where not C yet.",
          },
        ],
      },
      {
        id: "pblr-tx",
        label: "PBLR transactionId",
        actions: [
          {
            id: "pblr-tx-help",
            label: "help",
            cmd: "python pblr_tx.py --help",
            title: "--list retained ids. --trigger MeterValues. --trigger --publish retains them on lb/temp-tx_id.",
          },
          {
            id: "pblr-tx-list",
            label: "python pblr_tx.py --list",
            cmd: "python pblr_tx.py --list",
            title: "Paste only. Retained lb/temp-tx_id: charger and transactionId. Sends nothing.",
          },
          {
            id: "pblr-tx-trigger",
            label: "python pblr_tx.py --trigger",
            cmd: "python pblr_tx.py --trigger",
            title: "Paste only. y sends TriggerMessage MeterValues to every online PBLR, then prints each transactionId.",
          },
          {
            id: "pblr-tx-publish",
            label: "python pblr_tx.py --trigger --publish",
            cmd: "python pblr_tx.py --trigger --publish",
            title: "Paste only. Same trigger, then retain each retrieved id on lb/temp-tx_id/{id} as ocpp-core.",
          },
        ],
      },
    ],
  },
  {
    id: "power-recalculation",
    label: "power-recalculation",
    dir: "power-recalculation",
    groups: [
      {
        id: "MV-history",
        label: "MV-history",
        actions: [
          {
            id: "mv-help",
            label: "help",
            cmd: "python mv_history_power.py --help",
            title: "MeterValues log → 1-min power (usage)",
          },
          {
            id: "mv-run-fleet",
            label: "run fleet (edit times)",
            cmd: "python mv_history_power.py --start 2026-07-27T07:30:00Z --stop 2026-07-27T07:45:00Z --fleet",
            title: "Edit --start/--stop UTC, then Enter; summary + fleet 1-min peek",
          },
          {
            id: "mv-run-csv",
            label: "run → /tmp CSV (edit times)",
            cmd: "python mv_history_power.py --start 2026-07-27T07:30:00Z --stop 2026-07-27T07:45:00Z --fleet --csv /tmp/power_1min_fleet.csv --summary",
            title: "Edit times; write fleet 1-min CSV under /tmp",
          },
        ],
      },
    ],
  },
  {
    id: "walk-ocpp-logs",
    label: "walk ocpp logs",
    dir: "walk-ocpp-logs",
    groups: [
      {
        id: "scan-db",
        label: "scan db",
        actions: [
          {
            id: "walk-help",
            label: "help",
            cmd: "python walk_ocpp_logs.py --help",
            title: "--refresh --days N  --fw [--match VER]  --family BCU12|BCU34|PBLR  --limit N  --online  --apply",
          },
          {
            id: "walk-refresh-bcu12",
            label: "python walk_ocpp_logs.py --refresh --days 14 --family BCU12 --apply",
            cmd: "python walk_ocpp_logs.py --refresh --days 14 --family BCU12 --apply",
            title: "Paste only. Walk BootNotification; store last FW per charger in charger_firmware.",
          },
          {
            id: "walk-refresh-all",
            label: "python walk_ocpp_logs.py --refresh --days 7 --family BCU12 BCU34 PBLR --apply",
            cmd: "python walk_ocpp_logs.py --refresh --days 7 --family BCU12 BCU34 PBLR --apply",
            title: "Paste only. Edit --days/--family. Last FW for all families → SQLite.",
          },
        ],
      },
      {
        id: "query-db",
        label: "query db",
        actions: [
          {
            id: "walk-fw-all",
            label: "python walk_ocpp_logs.py --fw",
            cmd: "python walk_ocpp_logs.py --fw",
            title: "Paste only. Latest FW per charger + counts by version (charger_firmware).",
          },
          {
            id: "walk-fw-451",
            label: "python walk_ocpp_logs.py --fw --match 4.51",
            cmd: "python walk_ocpp_logs.py --fw --match 4.51",
            title: "Paste only. Chargers whose latest FW is 4.51 (edit --match for other versions).",
          },
          {
            id: "walk-fw-451-bcu12",
            label: "python walk_ocpp_logs.py --fw --match 4.51 --family BCU12",
            cmd: "python walk_ocpp_logs.py --fw --match 4.51 --family BCU12",
            title: "Paste only. 4.51 on BCU12 only.",
          },
        ],
      },
    ],
  },
  {
    id: "infra",
    label: "infra",
    dir: "infra",
    groups: [
      {
        id: "restart",
        label: "restart",
        actions: [
          {
            id: "restart-ls",
            label: "ls",
            cmd: "ls",
            title: "Paste ls only — no Enter; confirm the line waits for you",
          },
          {
            id: "restart-readme",
            label: "RESTART.md",
            cmd: "cat /opt/fcr/dashboard/scripts/restarts/RESTART.md",
            title: "Paste only. Show restart sequence doc.",
          },
          {
            id: "restart-check-mqtt-state",
            label: "check mqtt state",
            cmd: "python /opt/fcr/dashboard/scripts/mqtt/mqtt_state.py --count --timeout 8",
            title:
              "Paste only. After EMQX + complete gateway: expect non-zero gw/ws/state.",
          },
          {
            id: "restart-nginx-reload",
            label: "nginx reload",
            cmd: 'read -q "?Reload nginx (nginx -t && nginx -s reload)? [y/N] " && sudo /opt/homebrew/opt/nginx/bin/nginx -t && sudo /opt/homebrew/opt/nginx/bin/nginx -s reload && echo "--- com.fcr.nginx ---" && sudo launchctl print system/com.fcr.nginx | grep -E \'state =|pid =|last exit\'',
            title: "Paste only. Preferred: config reload without full process drop. y to run.",
          },
          {
            id: "restart-nginx-kickstart",
            label: "nginx kickstart",
            cmd: 'read -q "?HARD restart nginx (kickstart -k, brief :80/:443 drop)? [y/N] " && sudo launchctl kickstart -k system/com.fcr.nginx && echo "--- com.fcr.nginx ---" && sudo launchctl print system/com.fcr.nginx | grep -E \'state =|pid =|last exit\'',
            title: "Paste only. Full process restart. Prefer nginx reload when possible.",
          },
          {
            id: "restart-emqx",
            label: "emqx",
            cmd: 'read -q "?Restart EMQX (HIGH IMPACT — drops all MQTT clients)? [y/N] " && sudo launchctl kickstart -k system/com.fcr.emqx && echo "--- com.fcr.emqx ---" && sudo launchctl print system/com.fcr.emqx | grep -E \'state =|pid =|last exit\'',
            title: "Paste only. HIGH IMPACT: all MQTT clients disconnect.",
          },
          {
            id: "restart-questdb",
            label: "questdb",
            cmd: 'read -q "?Restart QuestDB (freq writers pause until back)? [y/N] " && sudo launchctl kickstart -k system/com.fcr.questdb && echo "--- com.fcr.questdb ---" && sudo launchctl print system/com.fcr.questdb | grep -E \'state =|pid =|last exit\'',
            title: "Paste only. Time-series DB restart.",
          },
          {
            id: "restart-ocpp-core",
            label: "ocpp-core",
            cmd: 'read -q "?Restart ocpp-core (edge keeps charger WS open)? [y/N] " && sudo launchctl kickstart -k system/com.fcr.ocpp-core && echo "--- com.fcr.ocpp-core ---" && sudo launchctl print system/com.fcr.ocpp-core | grep -E \'state =|pid =|last exit\'',
            title: "Paste only. Safe alone — chargers stay connected on edge.",
          },
          {
            id: "restart-ocpp-edge",
            label: "ocpp-edge",
            cmd: 'read -q "?Restart ocpp-edge (HIGH IMPACT — drops all charger WS)? [y/N] " && sudo launchctl kickstart -k system/com.fcr.ocpp-edge && echo "--- com.fcr.ocpp-edge ---" && sudo launchctl print system/com.fcr.ocpp-edge | grep -E \'state =|pid =|last exit\'',
            title: "Paste only. HIGH IMPACT: drops every charger WebSocket.",
          },
          {
            id: "restart-ocpp-gateway",
            label: "ocpp-gateway (complete)",
            cmd: 'read -q "?Restart ocpp-gateway completely (drops all charger WS)? [y/N] " && sudo launchctl kickstart -k system/com.fcr.ocpp-core && sudo launchctl kickstart -k system/com.fcr.ocpp-edge && echo "--- com.fcr.ocpp-core ---" && sudo launchctl print system/com.fcr.ocpp-core | grep -E \'state =|pid =|last exit\' && echo "--- com.fcr.ocpp-edge ---" && sudo launchctl print system/com.fcr.ocpp-edge | grep -E \'state =|pid =|last exit\'',
            title:
              "Paste only. After Enter: type y to run (core then edge; drops all charger WS).",
          },
          {
            id: "restart-dashboard",
            label: "dashboard",
            cmd: 'read -q "?Restart FCR dashboard (com.fcr.dashboard)? [y/N] " && sudo launchctl kickstart -k system/com.fcr.dashboard && echo "--- com.fcr.dashboard ---" && sudo launchctl print system/com.fcr.dashboard | grep -E \'state =|pid =|last exit\'',
            title: "Paste only. Brief UI/API blip; devops shell will disconnect.",
          },
          {
            id: "restart-infra-monitor",
            label: "infra-monitor",
            cmd: 'read -q "?Restart infra-monitor? [y/N] " && sudo launchctl kickstart -k system/com.fcr.infra-monitor && echo "--- com.fcr.infra-monitor ---" && sudo launchctl print system/com.fcr.infra-monitor | grep -E \'state =|pid =|last exit\'',
            title: "Paste only. Infra MQTT / health monitor.",
          },
          {
            id: "restart-freq-service",
            label: "freq-service",
            cmd: 'read -q "?Restart freq-service? [y/N] " && sudo launchctl kickstart -k system/com.fcr.freq-service && echo "--- com.fcr.freq-service ---" && sudo launchctl print system/com.fcr.freq-service | grep -E \'state =|pid =|last exit\'',
            title: "Paste only. Frequency arbitration / QuestDB writer.",
          },
          {
            id: "restart-freq-meter-main-a",
            label: "freq-meter-main-a",
            cmd: 'read -q "?Restart freq-meter-main-a (Modbus main_A)? [y/N] " && sudo launchctl kickstart -k system/com.fcr.freq-meter-main-a && echo "--- com.fcr.freq-meter-main-a ---" && sudo launchctl print system/com.fcr.freq-meter-main-a | grep -E \'state =|pid =|last exit\'',
            title: "Paste only. Canonical Modbus frequency meter A.",
          },
          {
            id: "restart-freq-meter-main-c",
            label: "freq-meter-main-c",
            cmd: 'read -q "?Restart freq-meter-main-c (UDP backup main_C)? [y/N] " && sudo launchctl kickstart -k system/com.fcr.freq-meter-main-c && echo "--- com.fcr.freq-meter-main-c ---" && sudo launchctl print system/com.fcr.freq-meter-main-c | grep -E \'state =|pid =|last exit\'',
            title: "Paste only. UDP :8000 backup meter C.",
          },
        ],
      },
      {
        id: "shell",
        label: "shell",
        actions: [
          {
            id: "infra-ls",
            label: "ls",
            cmd: "ls -la",
            title: "List scripts/infra",
          },
        ],
      },
    ],
  },
];
type NavState = { domainId: string | null; groupId: string | null };

function loadNav(): NavState {
  // Never auto-select an area on open — selection must come with an explicit click (and cd).
  return { domainId: null, groupId: null };
}

function devopsWsUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws/devops`;
}

function loadFontSize(): number {
  try {
    const n = Number(localStorage.getItem(FONT_KEY));
    if (Number.isFinite(n) && n >= FONT_MIN && n <= FONT_MAX) return Math.round(n);
  } catch {
    /* ignore */
  }
  return FONT_DEFAULT;
}

const TERM_THEME = {
  background: "#0f1419",
  foreground: "#e6edf3",
  cursor: "#e6edf3",
  selectionBackground: "#264f78",
  black: "#0f1419",
  red: "#f97583",
  green: "#85e89d",
  yellow: "#ffea7f",
  blue: "#79b8ff",
  magenta: "#b392f0",
  cyan: "#9ecbff",
  white: "#e6edf3",
  brightBlack: "#6e7681",
  brightRed: "#f97583",
  brightGreen: "#85e89d",
  brightYellow: "#ffea7f",
  brightBlue: "#79b8ff",
  brightMagenta: "#b392f0",
  brightCyan: "#9ecbff",
  brightWhite: "#ffffff",
} as const;

export function DevopsView() {
  const hostRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<"connecting" | "open" | "closed" | "error">("connecting");
  const [reconnectToken, setReconnectToken] = useState(0);
  const [fontSize, setFontSize] = useState(loadFontSize);
  const [nav, setNav] = useState<NavState>(loadNav);

  const domain = useMemo(
    () => (nav.domainId ? QUICK_TREE.find((d) => d.id === nav.domainId) ?? null : null),
    [nav.domainId],
  );
  const group = useMemo(() => {
    if (!domain || !nav.groupId) return null;
    return domain.groups.find((g) => g.id === nav.groupId) ?? null;
  }, [domain, nav.groupId]);

  const runQuick = useCallback((cmd: string, opts?: { execute?: boolean }) => {
    const ws = wsRef.current;
    const term = termRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send("\u0015"); // Ctrl-U — clear current line
    // Paste only by default so you can edit; Area cd passes execute: true.
    ws.send(opts?.execute ? `${cmd}\n` : cmd);
    term?.focus();
  }, []);

  const selectDomain = useCallback(
    (domainId: string) => {
      const d = QUICK_TREE.find((x) => x.id === domainId);
      if (!d) return;
      const next = { domainId: d.id, groupId: d.groups[0]?.id ?? null };
      setNav(next);
      // Always cd from scripts root so it works no matter the current cwd.
      runQuick(`cd "$FCR_SCRIPTS_DIR/${d.dir}"`, { execute: true });
    },
    [runQuick],
  );

  const selectGroup = useCallback(
    (groupId: string) => {
      if (!domain) return;
      setNav({ domainId: domain.id, groupId });
    },
    [domain],
  );

  const applyFontSize = useCallback((size: number) => {
    const next = Math.min(FONT_MAX, Math.max(FONT_MIN, size));
    setFontSize(next);
    try {
      localStorage.setItem(FONT_KEY, String(next));
    } catch {
      /* ignore */
    }
    const term = termRef.current;
    const fit = fitRef.current;
    const ws = wsRef.current;
    if (!term || !fit) return;
    term.options.fontSize = next;
    fit.fit();
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }));
    }
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const term = new Terminal({
      cursorBlink: true,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize,
      lineHeight: 1.25,
      theme: TERM_THEME,
      allowProposedApi: true,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host);
    fit.fit();
    term.focus();

    termRef.current = term;
    fitRef.current = fit;
    setStatus("connecting");

    const ws = new WebSocket(devopsWsUrl());
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let fitTimer: ReturnType<typeof setTimeout> | null = null;

    const sendResize = () => {
      fit.fit();
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }));
      }
    };

    const scheduleFit = () => {
      if (fitTimer) clearTimeout(fitTimer);
      fitTimer = setTimeout(sendResize, 80);
    };

    ws.onopen = () => {
      setStatus("open");
      sendResize();
      pingTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping", t: Date.now() }));
        }
      }, PING_MS);
    };
    ws.onmessage = (ev) => {
      const data = typeof ev.data === "string" ? ev.data : new TextDecoder().decode(ev.data);
      if (data.startsWith("{")) {
        try {
          const msg = JSON.parse(data);
          if (msg?.type === "pong") return;
        } catch {
          /* fall through */
        }
      }
      term.write(data);
    };
    ws.onerror = () => {
      setStatus("error");
      term.writeln("\r\n[websocket error]");
    };
    ws.onclose = (ev) => {
      setStatus("closed");
      if (pingTimer) clearInterval(pingTimer);
      term.writeln(`\r\n[disconnected${ev.reason ? `: ${ev.reason}` : ""}]`);
    };

    const onData = term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(data);
    });

    window.addEventListener("resize", scheduleFit);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleFit) : null;
    ro?.observe(host);

    return () => {
      onData.dispose();
      window.removeEventListener("resize", scheduleFit);
      ro?.disconnect();
      if (pingTimer) clearInterval(pingTimer);
      if (fitTimer) clearTimeout(fitTimer);
      try {
        ws.close();
      } catch {
        /* ignore */
      }
      wsRef.current = null;
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectToken]);

  const btnClass =
    "rounded border border-border-subtle px-2 py-1 text-xs text-text-primary hover:bg-black/5 disabled:opacity-40";
  const chipBase =
    "rounded border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40";
  const chipIdle = `${chipBase} border-border-subtle bg-white text-text-secondary hover:bg-black/[0.04]`;
  const chipActive = `${chipBase} border-[#1f6feb] bg-[#1f6feb] text-white`;
  const actionClass =
    "rounded border border-border-subtle bg-white px-2 py-1 font-mono text-[11px] text-text-primary hover:bg-black/[0.04] disabled:opacity-40";

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-text-primary">Devops</h1>
          <p className="text-sm text-text-secondary">
            Real shell on m4-1 · <code className="text-xs">scripts/</code> · venv auto-on
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1" title="Terminal font size">
            <button
              type="button"
              className={btnClass}
              aria-label="Decrease font size"
              disabled={fontSize <= FONT_MIN}
              onClick={() => applyFontSize(fontSize - 1)}
            >
              A−
            </button>
            <span className="min-w-[2.5rem] text-center font-mono text-xs text-text-secondary">
              {fontSize}px
            </span>
            <button
              type="button"
              className={btnClass}
              aria-label="Increase font size"
              disabled={fontSize >= FONT_MAX}
              onClick={() => applyFontSize(fontSize + 1)}
            >
              A+
            </button>
          </div>
          <span className="font-mono text-xs text-text-secondary" title="WebSocket status">
            {status === "open"
              ? "connected"
              : status === "connecting"
                ? "connecting…"
                : status === "error"
                  ? "error"
                  : "disconnected"}
          </span>
          <button
            type="button"
            className={btnClass}
            onClick={() => setReconnectToken((n) => n + 1)}
          >
            Reconnect
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5 rounded-lg border border-border-subtle bg-page/40 px-3 py-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="w-10 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
            Area
          </span>
          {QUICK_TREE.map((d) => (
            <button
              key={d.id}
              type="button"
              className={domain?.id === d.id ? chipActive : chipIdle}
              disabled={status !== "open"}
              title={status === "open" ? `cd scripts/${d.dir}` : "Connect first"}
              onClick={() => selectDomain(d.id)}
            >
              {d.label}
            </button>
          ))}
          {!domain && (
            <span className="text-[11px] text-text-secondary">pick an area to cd + show groups</span>
          )}
        </div>
        {domain && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="w-10 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
              Group
            </span>
            {domain.groups.map((g) => (
              <button
                key={g.id}
                type="button"
                className={group?.id === g.id ? chipActive : chipIdle}
                onClick={() => selectGroup(g.id)}
              >
                {g.label}
              </button>
            ))}
          </div>
        )}
        {domain && group && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="w-10 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
              Run
            </span>
            {group.actions.map((a) => (
              <button
                key={a.id}
                type="button"
                className={actionClass}
                title={a.title ? `${a.title}\n$ ${a.cmd}` : a.cmd}
                disabled={status !== "open"}
                onClick={() => runQuick(a.cmd)}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        className="min-h-0 flex-1 overflow-hidden rounded-lg border border-border-subtle shadow-inner"
        style={{ background: "#0f1419" }}
      >
        <div ref={hostRef} className="h-full w-full p-2" />
      </div>

      <p className="text-xs text-text-secondary">
        {domain && group ? (
          <>
            <code>{domain.label}</code> / <code>{group.label}</code> · in{" "}
            <code>scripts/{domain.dir}</code>
          </>
        ) : (
          <>
            Shell starts in <code>scripts/</code> — click an area to cd into it
          </>
        )}
      </p>
    </div>
  );
}
